/**
 * Fatture dei consulenti, confrontate con i rendiconti approvati.
 *
 * Il consulente manda la fattura e qualcuno deve dire se è giusta. Qui si
 * registra il documento, si vede subito quanto il gestionale calcola di
 * dovergli — rendiconto per rendiconto, con commessa e periodo — e si decide:
 * verificata, contestata, pagata.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fattureConsulentiApi, consulentiApi, apriProtetto } from '../api';
import Modal from '../components/Modal';
import CampoImporto from '../components/CampoImporto';

const fmt = n => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(n || 0);
const fmtDate = d => d ? new Date(d).toLocaleDateString('it-IT') : '—';
const oggi = () => new Date().toISOString().slice(0, 10);

const STATO = {
  ricevuta:   { l: 'Da verificare', c: 'bg-amber-100 text-amber-800' },
  verificata: { l: 'Verificata', c: 'bg-green-100 text-green-700' },
  contestata: { l: 'Contestata', c: 'bg-red-100 text-red-700' },
  pagata:     { l: 'Pagata', c: 'bg-brand-100 text-brand-700' },
};

const GIUDIZIO = {
  coincide:      { l: 'coincide con i rendiconti', c: 'text-green-700' },
  chiede_di_piu: { l: 'chiede più di quanto risulta', c: 'text-red-700' },
  chiede_di_meno:{ l: 'chiede meno di quanto risulta', c: 'text-amber-700' },
  da_verificare: { l: 'da confrontare', c: 'text-gray-500' },
};

const vuota = { consulente_id: '', numero: '', data: oggi(), importo: '',
                periodo_da: '', periodo_a: '', note: '' };

export default function FattureConsulenti() {
  const [righe, setRighe] = useState([]);
  const [consulenti, setConsulenti] = useState([]);
  const [modal, setModal] = useState(null);      // { mode: 'new'|'edit', id }
  const [form, setForm] = useState(vuota);
  const [file, setFile] = useState(null);
  const [anteprima, setAnteprima] = useState(null);
  const [dettaglio, setDettaglio] = useState(null);
  const [lavorando, setLavorando] = useState(false);

  const carica = () => fattureConsulentiApi.list().then(setRighe).catch(() => setRighe([]));
  useEffect(() => { carica(); consulentiApi.list().then(setConsulenti).catch(() => setConsulenti([])); }, []);

  // Il confronto si aggiorna mentre si compila: è il motivo per cui si apre
  // questa finestra, quindi non deve aspettare il salvataggio.
  useEffect(() => {
    if (!modal || !form.consulente_id) { setAnteprima(null); return; }
    let vivo = true;
    fattureConsulentiApi.confronto({
      consulente_id: form.consulente_id, da: form.periodo_da || undefined,
      a: form.periodo_a || undefined, importo: form.importo || undefined,
    }).then(d => { if (vivo) setAnteprima(d); }).catch(() => {});
    return () => { vivo = false; };
  }, [modal, form.consulente_id, form.periodo_da, form.periodo_a, form.importo]);

  const apri = (r) => {
    setFile(null); setAnteprima(null);
    setForm(r ? { ...r, data: r.data?.slice(0, 10) || '', periodo_da: r.periodo_da?.slice(0, 10) || '',
                  periodo_a: r.periodo_a?.slice(0, 10) || '', importo: r.importo }
              : { ...vuota });
    setModal({ mode: r ? 'edit' : 'new', id: r?.id });
  };

  const salva = async () => {
    if (!form.consulente_id) return alert('Indica di chi è la fattura');
    setLavorando(true);
    try {
      if (modal.mode === 'new') await fattureConsulentiApi.create(form, file);
      else await fattureConsulentiApi.update(modal.id, form, file);
      setModal(null); setFile(null); carica();
    } catch (e) { alert(e); } finally { setLavorando(false); }
  };

  const vedi = async (r) => {
    try { setDettaglio(await fattureConsulentiApi.get(r.id)); } catch (e) { alert(e); }
  };

  const cambiaStato = async (id, stato, nota) => {
    setLavorando(true);
    try {
      await fattureConsulentiApi.stato(id, { stato, nota_verifica: nota });
      if (dettaglio?.id === id) setDettaglio(await fattureConsulentiApi.get(id));
      carica();
    } catch (e) { alert(e); } finally { setLavorando(false); }
  };

  const imputa = async (id) => {
    if (!confirm('Registrare il costo di questa fattura nelle commesse interessate?\n\n'
               + 'Nasce una riga di costo per ogni rendiconto coperto: serve a far quadrare il margine.')) return;
    setLavorando(true);
    try {
      const r = await fattureConsulentiApi.imputaCosti(id);
      alert(`Registrate ${r.creati} righe di costo per ${fmt(r.totale)}.`);
      if (dettaglio?.id === id) setDettaglio(await fattureConsulentiApi.get(id));
      carica();
    } catch (e) { alert(e); } finally { setLavorando(false); }
  };

  const elimina = async (r) => {
    if (!confirm(`Eliminare la fattura ${r.numero || 'senza numero'} di ${r.consulente}?`)) return;
    try { await fattureConsulentiApi.delete(r.id); carica(); } catch (e) { alert(e); }
  };

  const f = (k) => ({ value: form[k] ?? '', onChange: e => setForm(p => ({ ...p, [k]: e.target.value })) });
  const daVerificare = righe.filter(r => r.stato === 'ricevuta');

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="page-title">Fatture dei consulenti</h1>
        <button className="btn-primary" onClick={() => apri(null)}>+ Registra fattura</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card">
          <div className="text-sm text-gray-500">Da verificare</div>
          <div className="text-2xl font-bold text-amber-600 mt-1">{fmt(daVerificare.reduce((t, r) => t + Number(r.importo), 0))}</div>
          <div className="text-xs text-gray-400 mt-0.5">{daVerificare.length} fatture ricevute</div>
        </div>
        <div className="card">
          <div className="text-sm text-gray-500">Non coincidono</div>
          <div className="text-2xl font-bold text-red-600 mt-1">
            {righe.filter(r => r.giudizio && r.giudizio !== 'coincide' && r.stato !== 'pagata').length}
          </div>
          <div className="text-xs text-gray-400 mt-0.5">differenza rispetto ai rendiconti</div>
        </div>
        <div className="card">
          <div className="text-sm text-gray-500">Verificate da pagare</div>
          <div className="text-2xl font-bold text-brand-600 mt-1">
            {fmt(righe.filter(r => r.stato === 'verificata').reduce((t, r) => t + Number(r.importo), 0))}
          </div>
          <div className="text-xs text-gray-400 mt-0.5">controllate, non ancora pagate</div>
        </div>
      </div>

      <div className="card p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr className="text-left text-xs text-gray-500">
              <th className="px-4 py-3">Consulente</th>
              <th className="px-4 py-3">Fattura</th>
              <th className="px-4 py-3">Periodo</th>
              <th className="px-4 py-3 text-right">Chiede</th>
              <th className="px-4 py-3 text-right">Risulta</th>
              <th className="px-4 py-3 text-right">Differenza</th>
              <th className="px-4 py-3">Stato</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {righe.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">
                Nessuna fattura registrata. Quando un consulente te ne manda una, registrala qui:
                il confronto con i rendiconti è automatico.
              </td></tr>
            )}
            {righe.map(r => {
              const st = STATO[r.stato] || STATO.ricevuta;
              const g = GIUDIZIO[r.giudizio] || GIUDIZIO.da_verificare;
              const diff = Number(r.differenza || 0);
              return (
                <tr key={r.id} className="border-b last:border-0 hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">{r.consulente}</td>
                  <td className="px-4 py-3">
                    <div className="font-mono text-xs text-gray-700">{r.numero || 'senza numero'}</div>
                    <div className="text-xs text-gray-500">{fmtDate(r.data)}</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600">
                    {r.periodo_da || r.periodo_a
                      ? `${fmtDate(r.periodo_da)} – ${fmtDate(r.periodo_a)}`
                      : <span className="text-gray-400">non indicato</span>}
                    <div className="text-[11px] text-gray-400">{r.rendiconti} rendiconti coperti</div>
                  </td>
                  <td className="px-4 py-3 text-right font-medium">{fmt(r.importo)}</td>
                  <td className="px-4 py-3 text-right text-gray-700">{fmt(r.atteso)}</td>
                  <td className={`px-4 py-3 text-right font-semibold ${
                    Math.abs(diff) < 0.01 ? 'text-green-700' : diff > 0 ? 'text-red-600' : 'text-amber-600'}`}>
                    {Math.abs(diff) < 0.01 ? '—' : (diff > 0 ? '+' : '') + fmt(diff)}
                    <div className={`text-[11px] font-normal ${g.c}`}>{g.l}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${st.c}`}>{st.l}</span>
                    {r.costi_registrati > 0 && (
                      <div className="text-[11px] text-gray-400 mt-0.5">costo imputato</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1 justify-end">
                      <button className="btn-secondary btn-sm" onClick={() => vedi(r)}>Confronta</button>
                      {r.ha_allegato && (
                        <button className="btn-secondary btn-sm" title="Apri il documento"
                                onClick={() => apriProtetto(`/fatture-consulenti/${r.id}/allegato`,
                                                            r.allegato_nome).catch(e => alert(e))}>📎</button>
                      )}
                      <button className="btn-secondary btn-sm px-2" onClick={() => apri(r)}>✎</button>
                      <button className="btn-danger btn-sm px-2" onClick={() => elimina(r)}>🗑</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Registrazione / correzione, con il confronto che si aggiorna da sé */}
      {modal && (
        <Modal title={modal.mode === 'new' ? 'Registra la fattura del consulente' : 'Correggi la fattura'}
               onClose={() => setModal(null)} size="lg">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Consulente *</label>
              <select className="input" value={form.consulente_id || ''}
                      onChange={e => setForm(p => ({ ...p, consulente_id: e.target.value }))}>
                <option value="">— scegli —</option>
                {consulenti.map(c => <option key={c.id} value={c.id}>{c.nome} {c.cognome}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Documento</label>
              <input type="file" className="input py-1 text-sm" accept=".pdf,.doc,.docx,.jpg,.png"
                     onChange={e => setFile(e.target.files?.[0] || null)} />
              <p className="text-xs text-gray-400 mt-0.5">
                Numero, data e importo vengono letti dal documento e restano correggibili.
              </p>
            </div>
            <div><label className="label">Numero</label><input className="input" {...f('numero')} /></div>
            <div><label className="label">Data</label><input className="input" type="date" {...f('data')} /></div>
            <div>
              <label className="label">Importo (imponibile)</label>
              <CampoImporto className="input" value={form.importo}
                            onChange={v => setForm(p => ({ ...p, importo: v }))} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="label">Periodo dal</label>
                <input className="input" type="date" {...f('periodo_da')} />
              </div>
              <div>
                <label className="label">al</label>
                <input className="input" type="date" {...f('periodo_a')} />
              </div>
            </div>
            <div className="col-span-2"><label className="label">Note</label>
              <input className="input" {...f('note')} /></div>
          </div>

          {anteprima && <Confronto dati={anteprima} importo={form.importo} />}

          <div className="flex justify-end gap-2 mt-5">
            <button className="btn-secondary" onClick={() => setModal(null)}>Annulla</button>
            <button className="btn-primary" onClick={salva} disabled={lavorando}>
              {lavorando ? 'Salvo…' : 'Salva'}
            </button>
          </div>
        </Modal>
      )}

      {/* Confronto di una fattura già registrata, con le decisioni */}
      {dettaglio && (
        <Modal title={`${dettaglio.consulente} — fattura ${dettaglio.numero || 'senza numero'}`}
               onClose={() => setDettaglio(null)} size="xl">
          <Confronto dati={dettaglio.confronto} importo={dettaglio.importo} esteso />

          {dettaglio.nota_verifica && (
            <p className="text-xs text-gray-600 mt-3 bg-gray-50 border rounded p-2">
              Nota: {dettaglio.nota_verifica}
            </p>
          )}

          <div className="flex flex-wrap gap-2 mt-5 pt-3 border-t">
            <button className="btn-sm btn bg-green-100 text-green-700 hover:bg-green-200 font-semibold"
                    disabled={lavorando} onClick={() => cambiaStato(dettaglio.id, 'verificata')}>
              ✓ Verificata
            </button>
            <button className="btn-sm btn bg-red-100 text-red-600 hover:bg-red-200"
                    disabled={lavorando}
                    onClick={() => {
                      const nota = prompt('Cosa non torna? Lo riporterai al consulente:');
                      if (nota?.trim()) cambiaStato(dettaglio.id, 'contestata', nota.trim());
                    }}>
              ✕ Contestata
            </button>
            <button className="btn-secondary btn-sm" disabled={lavorando}
                    onClick={() => cambiaStato(dettaglio.id, 'pagata')}>
              € Pagata
            </button>
            <button className="btn-secondary btn-sm ml-auto" disabled={lavorando}
                    onClick={() => imputa(dettaglio.id)}
                    title="Crea le righe di costo nelle commesse, così il margine è quello vero">
              Registra il costo in commessa
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/** Il confronto vero e proprio: quanto risulta, quanto chiede, dove differisce. */
function Confronto({ dati, importo, esteso }) {
  const dichiarato = Number(importo || dati.dichiarato || 0);
  const diff = +(dichiarato - Number(dati.atteso || 0)).toFixed(2);
  const coincide = Math.abs(diff) < 0.01;

  return (
    <div className="mt-4 space-y-3">
      <div className={`border rounded-lg p-3 ${
        coincide ? 'bg-green-50 border-green-200'
        : diff > 0 ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
        <div className="flex flex-wrap gap-x-8 gap-y-1 text-sm">
          <span>Risulta dai rendiconti: <strong>{fmt(dati.atteso)}</strong></span>
          <span>La fattura chiede: <strong>{fmt(dichiarato)}</strong></span>
          <span className={coincide ? 'text-green-700' : diff > 0 ? 'text-red-700' : 'text-amber-700'}>
            Differenza: <strong>{coincide ? 'nessuna' : (diff > 0 ? '+' : '') + fmt(diff)}</strong>
          </span>
        </div>
        {!coincide && (
          <p className="text-xs mt-1.5 text-gray-600">
            {diff > 0
              ? 'Chiede più di quanto risulta approvato: controlla se mancano rendiconti da approvare, oppure chiedi il dettaglio.'
              : 'Chiede meno di quanto risulta: forse ha dimenticato qualcosa, o una parte la fattura più avanti.'}
          </p>
        )}
      </div>

      {dati.rendiconti.length > 0 && (
        <div className="border rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-3 py-1.5 text-xs font-semibold text-gray-600 border-b">
            Rendiconti approvati del periodo
          </div>
          <table className="w-full text-sm">
            <tbody>
              {dati.rendiconti.map(s => (
                <tr key={s.sal_id} className="border-b last:border-0">
                  <td className="px-3 py-1.5 text-xs text-gray-500 w-24">{fmtDate(s.data_sal)}</td>
                  <td className="px-3 py-1.5">
                    <Link to={`/sal?apri=${s.sal_id}`} className="text-brand-600 hover:underline">
                      {s.commessa_numero}
                    </Link>
                    <span className="text-gray-500 text-xs"> · {s.cliente_nome}</span>
                    <div className="text-xs text-gray-500 truncate max-w-[320px]">{s.descrizione}</div>
                  </td>
                  <td className="px-3 py-1.5 text-right text-xs text-gray-500">
                    {s.spese > 0 && <>di cui spese {fmt(s.spese)}</>}
                  </td>
                  <td className="px-3 py-1.5 text-right font-medium w-28">{fmt(s.compenso)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {dati.da_approvare?.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
          <strong>{dati.da_approvare.length} rendiconti dello stesso periodo non sono approvati</strong>
          {' '}({fmt(dati.totale_da_approvare)}). Se la fattura li comprende, approvali prima di pagarla.
          {esteso && (
            <ul className="text-xs text-amber-800 mt-1 space-y-0.5">
              {dati.da_approvare.map(s => (
                <li key={s.sal_id}>
                  {fmtDate(s.data_sal)} · {s.commessa_numero} · {s.descrizione} — {fmt(s.compenso)} ({s.stato})
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {dati.gia_fatturati?.length > 0 && (
        <div className="bg-gray-50 border rounded-lg p-3 text-xs text-gray-600">
          {dati.gia_fatturati.length} rendiconti del periodo risultano già coperti da un'altra fattura
          ({fmt(dati.totale_gia_fatturato)}): esclusi dal conto.
        </div>
      )}

      {dati.costi_gia_registrati?.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
          Attenzione: nello stesso periodo ci sono già {dati.costi_gia_registrati.length} costi imputati
          a mano per questo consulente. Registrare anche questa fattura raddoppierebbe il costo.
        </div>
      )}
    </div>
  );
}
